import { TextInput } from "@jugl-web/ui-components";
import React, { useCallback, useState } from "react";
import { useTranslations, useToast } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { MAX_NAME_LENGTH } from "@web-src/modules/auth/conts";
import { Button } from "@jugl-web/ui-kit";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface CXProfileFormProps {
  onSuccess: () => void;
}

export const CXProfileForm: React.FC<CXProfileFormProps> = ({ onSuccess }) => {
  const { t } = useTranslations();
  const { toast } = useToast({ variant: "web" });
  const { usersApi } = useRestApiProvider();

  const [createProfile] = usersApi.useCreateProfileMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleProfileSubmit = useCallback(async () => {
    setIsSubmitted(true);
    if (!firstName.trim() || !lastName.trim()) return;

    setIsLoading(true);
    try {
      await createProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }).unwrap();
      onSuccess();
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong.",
        }),
        { variant: "error" }
      );
    } finally {
      setIsLoading(false);
    }
  }, [createProfile, firstName, lastName, onSuccess, t, toast]);

  return (
    <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
      <span className="font-secondary text-[32px] leading-[48px] font-medium text-dark">
        {t({
          id: "cx-ai-agents-page.lets-start-with-essentials",
          defaultMessage: "Let's start with essentials",
        })}
      </span>
      <span className="mt-2 font-secondary text-sm leading-4 text-grey-900">
        {t({
          id: "cx-ai-agents-page.just-a-few-details",
          defaultMessage: "Just a few details to start with",
        })}
      </span>
      <div className="mt-10 flex w-full flex-col gap-6">
        <div className="flex gap-4">
          <TextInput
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            label={t({ id: "common.first-name", defaultMessage: "First Name" })}
            isRequired
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            isInvalid={isSubmitted && !firstName.trim()}
            classNames={{ wrapperClassName: "flex-1" }}
            maxLength={MAX_NAME_LENGTH}
          />
          <TextInput
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            label={t({ id: "common.last-name", defaultMessage: "Last Name" })}
            isRequired
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            isInvalid={isSubmitted && !lastName.trim()}
            classNames={{ wrapperClassName: "flex-1" }}
            maxLength={MAX_NAME_LENGTH}
          />
        </div>
      </div>
      <Button
        className="mt-10 w-[500px]"
        onClick={handleProfileSubmit}
        isDisabled={!firstName.trim() || !lastName.trim() || isLoading}
        isLoading={isLoading}
        iconEnd={<ArrowIcon />}
      >
        {t({ id: "common.continue", defaultMessage: "Continue" })}
      </Button>
    </div>
  );
};
